// src/hooks/useWebSocketNotifications.ts
import { useContext, useEffect, useState } from "react";
import { Socket } from "socket.io-client";

import { SocketEvents } from "../lib/enums/SocketEvents";
import { AuthContext } from "../components/AuthContextComponent";
import { getSocket } from "../lib/helpers/SocketClient";

let socket: Socket | null = null;

export const useWebSocketNotifications = (
  username?: string,
  onNotifyResponse?: (data: any) => void
) => {
  const { loggedIn } = useContext(AuthContext);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!loggedIn || !username) {
      console.log("🔒 Not logged in or no username. Skipping socket init.");
      return;
    }

    if (!socket) {
      console.log("🔌 Initializing socket (from shared client)...");
      socket = getSocket();
    }
    // Ensure we have a single socket instance across the app
    const handleConnect = () => {
      setConnected(true);
      if (socket) {
        console.log("✅ Socket connected:", socket.id);
        socket.emit(SocketEvents.SETUP, username);
      }
    };
    // Handle socket connection and disconnection
    const handleDisconnect = (reason: string) => {
      setConnected(false);
      console.log("❌ Socket disconnected:", reason);
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);

    if (onNotifyResponse) {
      console.log("🛠️ Subscribing to NOTIFY_RESP");
      socket.on(SocketEvents.NOTIFY_RESP, onNotifyResponse);
    }

    return () => {
      if (socket) {
        socket.off("connect", handleConnect);
        socket.off("disconnect", handleDisconnect);

        if (onNotifyResponse) {
          console.log("🧹 Unsubscribing from NOTIFY_RESP");
          socket.off(SocketEvents.NOTIFY_RESP, onNotifyResponse);
        }
      }
    };
  }, [onNotifyResponse, username, loggedIn]);

  // Function to send a notification payload
  const sendNotification = (payload: any) => {
    if (socket && connected) {
      console.log("📤 Sending notification payload:", payload);
      socket.emit(SocketEvents.NOTIFY, payload);
    } else {
      console.warn("⚠️ Cannot send notification. Socket not connected.");
    }
  };

  // Function to disconnect the socket
  const disconnectSocket = () => {
    if (socket) {
      console.log("🚪 Disconnecting socket...");
      socket.disconnect();
      socket = null;
    }
  };

  return {
    sendNotification,
    socket,
    connected,
    disconnectSocket,
  };
};
